import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import {
  AlertCircle,
  ArrowRight,
  Check,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Plus,
  ShieldCheck,
  Terminal,
  Trash2,
  X,
} from 'lucide-react'
import { apiFetch, apiJson, orgPath } from '@/lib/api/client'
import { useAuth } from '@/lib/auth/useAuth'
import { formatDate } from '@/lib/utils'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet'
import { Eyebrow } from '@/components/hawk-primitives'

export const Route = createFileRoute('/_app/settings/tokens')({
  component: TokensPage,
})

interface Scopes {
  read: string[]
  write: string[]
}

interface RepoBrief {
  name: string
  ecosystem: string
}

interface TokenInfo {
  id: string
  name: string
  created_at: string
  last_used_at: string | null
  expires_at: string | null
  scopes: Scopes
}

interface CreatedToken extends TokenInfo {
  token: string
}

function TokensPage() {
  const queryClient = useQueryClient()
  const { currentOrgSlug } = useAuth()
  const [creating, setCreating] = useState(false)
  const [justCreated, setJustCreated] = useState<CreatedToken | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null)

  const tokens = useOrgQuery<TokenInfo[]>(['pat-tokens'], '/tokens')

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const r = await apiFetch(orgPath(`/tokens/${id}`), { method: 'DELETE' })
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pat-tokens', currentOrgSlug] })
      setConfirmRevoke(null)
    },
  })

  return (
    <div className="px-10 py-8">
      <PageHeader
        title="Tokens"
        description="Personal access tokens authenticate CLI tools (uv, pip, poetry, npm) to Hawk. Use them in CI or local dev. The browser session doesn't reach package downloads."
        right={
          tokens.data &&
          tokens.data.length > 0 && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              New token
            </Button>
          )
        }
      />

      <section className="animate-hawk-rise" style={{ animationDelay: '0ms' }}>
        <div className="mb-4 flex items-baseline gap-3">
          <Eyebrow>Personal access tokens</Eyebrow>
          {tokens.data && (
            <span className="font-mono text-xs text-muted-foreground tabular-nums">
              {tokens.data.length} active
            </span>
          )}
        </div>

        <div className="relative border border-border bg-card rounded-md overflow-hidden">
          <div
            className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent pointer-events-none"
            aria-hidden
          />

          {tokens.isLoading ? (
            <div className="p-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Loading tokens...
            </div>
          ) : tokens.data && tokens.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {tokens.data.map((tok) => (
                <li key={tok.id}>
                  <TokenRow
                    token={tok}
                    confirming={confirmRevoke === tok.id}
                    onAskConfirm={() => setConfirmRevoke(tok.id)}
                    onCancel={() => setConfirmRevoke(null)}
                    onRevoke={() => revoke.mutate(tok.id)}
                    revoking={revoke.isPending}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyTokens onCreate={() => setCreating(true)} />
          )}
        </div>
      </section>

      <CreateTokenSheet
        open={creating}
        onOpenChange={(open) => {
          setCreating(open)
          if (!open) setJustCreated(null)
        }}
        justCreated={justCreated}
        onCreated={(t) => {
          setJustCreated(t)
          queryClient.invalidateQueries({ queryKey: ['pat-tokens', currentOrgSlug] })
        }}
      />
    </div>
  )
}

function TokenRow({
  token,
  confirming,
  onAskConfirm,
  onCancel,
  onRevoke,
  revoking,
}: {
  token: TokenInfo
  confirming: boolean
  onAskConfirm: () => void
  onCancel: () => void
  onRevoke: () => void
  revoking: boolean
}) {
  const expired = !!token.expires_at && new Date(token.expires_at) <= new Date()
  return (
    <div className="group flex items-start gap-4 px-5 py-4 hover:bg-secondary/30 transition-colors">
      <div className="flex items-center justify-center size-9 rounded-md bg-primary/10 border border-primary/20 text-primary shrink-0 mt-0.5">
        <KeyRound className="size-4" strokeWidth={1.5} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-medium text-foreground truncate">
            {token.name}
          </div>
          {expired && (
            <span className="font-mono text-[10px] uppercase tracking-wider text-destructive px-1.5 py-0.5 rounded bg-destructive/10 ring-1 ring-inset ring-destructive/30">
              expired
            </span>
          )}
        </div>

        <div className="mt-1.5 flex items-center gap-x-3 gap-y-1 flex-wrap text-[11px] text-muted-foreground">
          <span>Created {formatDate(token.created_at)}</span>
          <span className="text-muted-foreground/40">·</span>
          {token.last_used_at ? (
            <span>Last used {formatDate(token.last_used_at)}</span>
          ) : (
            <span>Never used</span>
          )}
          {token.expires_at && !expired && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <span>Expires {formatDate(token.expires_at)}</span>
            </>
          )}
        </div>

        <div className="mt-2.5">
          <ScopeBadges scopes={token.scopes} />
        </div>
      </div>

      <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 data-[confirming=true]:opacity-100 transition-opacity shrink-0"
        data-confirming={confirming}
      >
        {confirming ? (
          <>
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mr-1">
              Revoke?
            </span>
            <Button
              size="sm"
              variant="destructive"
              onClick={onRevoke}
              disabled={revoking}
            >
              {revoking ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <Trash2 className="size-3" />
              )}
              Confirm
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onCancel}
              disabled={revoking}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button size="sm" variant="ghost" onClick={onAskConfirm}>
            Revoke
          </Button>
        )}
      </div>
    </div>
  )
}

function ScopeBadges({ scopes }: { scopes: Scopes }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <ScopeBadge label="read" perms={scopes.read} />
      <ScopeBadge label="write" perms={scopes.write} />
    </div>
  )
}

function ScopeBadge({ label, perms }: { label: 'read' | 'write'; perms: string[] }) {
  const isEmpty = perms.length === 0
  const isAll = perms.includes('*')
  const tone: 'muted' | 'ok' | 'warn' = isEmpty
    ? 'muted'
    : isAll && label === 'write'
      ? 'warn'
      : 'ok'
  const toneClass = {
    muted: 'bg-secondary/60 text-muted-foreground ring-border',
    ok: 'bg-primary/10 text-primary ring-primary/30',
    warn:
      'bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-amber-500/30',
  }[tone]
  const value = isEmpty ? 'none' : isAll ? 'all repos' : perms.join(', ')
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ring-1 ring-inset ${toneClass}`}
    >
      <span className="opacity-60">{label}</span>
      <span aria-hidden className="opacity-40">·</span>
      <span>{value}</span>
    </span>
  )
}

/* ----------------------------- Create sheet ----------------------------- */

type ScopeMode = 'all' | 'specific' | 'none'

function CreateTokenSheet({
  open,
  onOpenChange,
  justCreated,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  justCreated: CreatedToken | null
  onCreated: (t: CreatedToken) => void
}) {
  const [name, setName] = useState('')
  const [expiresDays, setExpiresDays] = useState<'never' | '30' | '90' | '365'>(
    'never',
  )
  const [readMode, setReadMode] = useState<ScopeMode>('all')
  const [readRepos, setReadRepos] = useState<string[]>([])
  const [writeMode, setWriteMode] = useState<ScopeMode>('none')
  const [writeRepos, setWriteRepos] = useState<string[]>([])

  const repos = useOrgQuery<RepoBrief[]>(['repos-brief'], '/repositories', {
    enabled: open,
  })

  function reset() {
    setName('')
    setExpiresDays('never')
    setReadMode('all')
    setReadRepos([])
    setWriteMode('none')
    setWriteRepos([])
  }

  const create = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim()
      if (!trimmed) throw new Error('Name is required')

      const scopes = {
        read: scopeList(readMode, readRepos),
        write: scopeList(writeMode, writeRepos),
      }
      if (scopes.read.length === 0 && scopes.write.length === 0) {
        throw new Error('Pick at least one scope')
      }
      if (readMode === 'specific' && readRepos.length === 0) {
        throw new Error('Pick at least one read repository')
      }
      if (writeMode === 'specific' && writeRepos.length === 0) {
        throw new Error('Pick at least one write repository')
      }

      const expires_at =
        expiresDays === 'never'
          ? null
          : new Date(
              Date.now() + parseInt(expiresDays, 10) * 86400_000,
            ).toISOString()

      const r = await apiFetch(orgPath('/tokens'), {
        method: 'POST',
        body: JSON.stringify({ name: trimmed, expires_at, scopes }),
      })
      if (!r.ok) throw new Error(await r.text())
      return (await r.json()) as CreatedToken
    },
    onSuccess: (t) => {
      onCreated(t)
      reset()
    },
  })

  const close = () => onOpenChange(false)

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) {
          reset()
          create.reset()
        }
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex flex-col gap-0 sm:max-w-lg"
      >
        {justCreated ? (
          <>
            <PanelHeader
              icon={<ShieldCheck className="size-4" strokeWidth={1.5} />}
              title="Token created"
              description="The raw value won't be shown again."
              onClose={close}
            />
            <CreatedView token={justCreated} onClose={close} />
          </>
        ) : (
          <>
            <PanelHeader
              icon={<KeyRound className="size-4" strokeWidth={1.5} />}
              title="New token"
              description="Authenticate uv / pip / poetry against Hawk."
              onClose={close}
            />

            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                <Field
                  label="Name"
                  hint="Where you'll use it"
                >
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={100}
                    placeholder="e.g. CI pull · laptop · ops bot"
                    autoFocus
                    className="text-sm"
                  />
                </Field>

                <Field label="Expires">
                  <Segmented
                    value={expiresDays}
                    onChange={(v) => setExpiresDays(v as typeof expiresDays)}
                    options={[
                      { value: 'never', label: 'Never' },
                      { value: '30', label: '30d' },
                      { value: '90', label: '90d' },
                      { value: '365', label: '1y' },
                    ]}
                  />
                </Field>

                <div className="space-y-3">
                  <Eyebrow>Permissions</Eyebrow>
                  <div className="rounded-md border border-border bg-card/40 divide-y divide-border overflow-hidden">
                    <PermissionRow
                      label="Read"
                      sublabel="Pull packages: index, metadata, files"
                      mode={readMode}
                      onModeChange={setReadMode}
                      selected={readRepos}
                      onSelectedChange={setReadRepos}
                      repos={repos.data}
                      reposLoading={repos.isLoading}
                    />
                    <PermissionRow
                      label="Write"
                      sublabel="Upload (twine, uv publish). Most CI tokens leave off."
                      mode={writeMode}
                      onModeChange={setWriteMode}
                      selected={writeRepos}
                      onSelectedChange={setWriteRepos}
                      repos={repos.data}
                      reposLoading={repos.isLoading}
                      warnAll
                    />
                  </div>
                </div>

                {create.error && (
                  <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30">
                    <AlertCircle
                      className="size-4 text-destructive shrink-0 mt-0.5"
                      strokeWidth={1.5}
                    />
                    <p className="text-xs text-destructive flex-1 leading-relaxed">
                      {create.error.message}
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-border bg-background/40 px-6 py-4 flex items-center justify-between gap-2">
                <SummaryLine
                  name={name}
                  readMode={readMode}
                  writeMode={writeMode}
                />
                <div className="flex items-center gap-2 shrink-0">
                  <SheetClose asChild>
                    <Button type="button" variant="ghost">
                      Cancel
                    </Button>
                  </SheetClose>
                  <Button
                    onClick={() => create.mutate()}
                    disabled={create.isPending || !name.trim()}
                  >
                    {create.isPending ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <KeyRound className="size-4" strokeWidth={1.5} />
                        Create token
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

function PanelHeader({
  icon,
  title,
  description,
  onClose,
}: {
  icon: React.ReactNode
  title: string
  description: string
  onClose: () => void
}) {
  return (
    <div className="relative px-6 pt-6 pb-5 border-b border-border">
      <div
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent pointer-events-none"
        aria-hidden
      />
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="size-9 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center text-primary shrink-0">
            {icon}
          </div>
          <div className="min-w-0">
            <SheetTitle className="text-base tracking-tight">
              {title}
            </SheetTitle>
            <SheetDescription className="text-xs mt-0.5">
              {description}
            </SheetDescription>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          className="text-muted-foreground -mr-2 -mt-1"
        >
          <X className="size-4" />
          <span className="sr-only">Close</span>
        </Button>
      </div>
    </div>
  )
}

function PermissionRow({
  label,
  sublabel,
  mode,
  onModeChange,
  selected,
  onSelectedChange,
  repos,
  reposLoading,
  warnAll,
}: {
  label: string
  sublabel: string
  mode: ScopeMode
  onModeChange: (m: ScopeMode) => void
  selected: string[]
  onSelectedChange: (s: string[]) => void
  repos: RepoBrief[] | undefined
  reposLoading: boolean
  warnAll?: boolean
}) {
  return (
    <div className="px-3 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">{label}</span>
            {warnAll && mode === 'all' && (
              <span className="font-mono text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded bg-amber-500/10 ring-1 ring-inset ring-amber-500/30">
                broad
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            {sublabel}
          </p>
        </div>
        <Segmented
          value={mode}
          onChange={(v) => onModeChange(v as ScopeMode)}
          options={[
            { value: 'none', label: 'None' },
            { value: 'all', label: 'All' },
            { value: 'specific', label: 'Specific' },
          ]}
        />
      </div>
      {mode === 'specific' && (
        <div className="mt-3">
          <RepoPicker
            selected={selected}
            onSelectedChange={onSelectedChange}
            repos={repos}
            loading={reposLoading}
          />
        </div>
      )}
    </div>
  )
}

function RepoPicker({
  selected,
  onSelectedChange,
  repos,
  loading,
}: {
  selected: string[]
  onSelectedChange: (s: string[]) => void
  repos: RepoBrief[] | undefined
  loading: boolean
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground px-2 py-2 rounded-md border border-border border-dashed">
        <Loader2 className="size-3 animate-spin" />
        Loading repositories…
      </div>
    )
  }
  if (!repos || repos.length === 0) {
    return (
      <div className="px-3 py-2.5 rounded-md border border-border border-dashed text-[11px] text-muted-foreground">
        No repositories configured yet. Ask an admin to add one.
      </div>
    )
  }
  function toggle(name: string) {
    onSelectedChange(
      selected.includes(name)
        ? selected.filter((n) => n !== name)
        : [...selected, name],
    )
  }
  return (
    <>
      <div className="flex flex-wrap gap-1.5">
        {repos.map((r) => {
          const active = selected.includes(r.name)
          return (
            <button
              key={r.name}
              type="button"
              role="checkbox"
              aria-checked={active}
              onClick={() => toggle(r.name)}
              className={
                'group inline-flex items-center gap-1.5 h-7 pl-1.5 pr-2.5 rounded-md text-xs font-medium transition-colors border ' +
                (active
                  ? 'bg-primary/15 text-primary border-primary/40 ring-1 ring-inset ring-primary/20'
                  : 'bg-background text-muted-foreground border-border hover:text-foreground hover:border-border')
              }
            >
              <span
                aria-hidden
                className={
                  'inline-flex items-center justify-center size-4 rounded-sm border transition-colors ' +
                  (active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border bg-background')
                }
              >
                {active && <Check className="size-3" strokeWidth={2.5} />}
              </span>
              <span className="font-mono">{r.name}</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.18em] opacity-50">
                {r.ecosystem}
              </span>
            </button>
          )
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground/70">
        <span>
          {selected.length} of {repos.length} selected
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onSelectedChange(repos.map((r) => r.name))}
            className="hover:text-foreground transition-colors"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={() => onSelectedChange([])}
            className="hover:text-foreground transition-colors"
          >
            Clear
          </button>
        </div>
      </div>
    </>
  )
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: string }[]
}) {
  return (
    <div
      role="radiogroup"
      className="inline-flex items-center rounded-md border border-border bg-background p-0.5 shrink-0"
    >
      {options.map((opt) => {
        const active = value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            className={
              'px-2.5 h-7 rounded text-xs font-medium transition-colors ' +
              (active
                ? 'bg-primary/15 text-primary ring-1 ring-inset ring-primary/30'
                : 'text-muted-foreground hover:text-foreground')
            }
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="grid gap-2">
      <label className="flex items-baseline justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        <span>{label}</span>
        {hint && (
          <span className="text-muted-foreground/50 normal-case tracking-normal">
            {hint}
          </span>
        )}
      </label>
      {children}
    </div>
  )
}

function SummaryLine({
  name,
  readMode,
  writeMode,
}: {
  name: string
  readMode: ScopeMode
  writeMode: ScopeMode
}) {
  const trimmed = name.trim()
  if (!trimmed) {
    return (
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground truncate">
        Name your token
      </span>
    )
  }
  return (
    <span className="font-mono text-xs text-muted-foreground truncate">
      <span className="text-foreground">{trimmed}</span>
      <span className="mx-1.5 opacity-40">·</span>
      <span className={readMode === 'none' ? '' : 'text-primary'}>
        read {modeLabel(readMode)}
      </span>
      <span className="mx-1.5 opacity-40">·</span>
      <span
        className={
          writeMode === 'all'
            ? 'text-amber-600 dark:text-amber-400'
            : writeMode === 'specific'
              ? 'text-primary'
              : ''
        }
      >
        write {modeLabel(writeMode)}
      </span>
    </span>
  )
}

function modeLabel(m: ScopeMode): string {
  return m === 'all' ? 'all' : m === 'specific' ? 'scoped' : 'none'
}

/* ---------------------------- Created view ---------------------------- */

function CreatedView({
  token,
  onClose,
}: {
  token: CreatedToken
  onClose: () => void
}) {
  const [revealed, setRevealed] = useState(true)
  const [copied, setCopied] = useState(false)

  function copy() {
    navigator.clipboard.writeText(token.token).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  const masked =
    token.token.slice(0, 13) + '…' + '•'.repeat(20)

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
        <div className="flex items-start gap-3 p-4 rounded-md bg-primary/5 ring-1 ring-inset ring-primary/30">
          <ShieldCheck
            className="size-5 text-primary shrink-0 mt-0.5"
            strokeWidth={1.5}
          />
          <div className="text-sm leading-relaxed">
            <span className="text-foreground font-medium">{token.name}</span>{' '}
            is active. Store the value below somewhere safe; Hawk only keeps a
            hash, so this is the only time you'll see it.
          </div>
        </div>

        <div>
          <Eyebrow marker>Token</Eyebrow>
          <div className="mt-2 rounded-md border border-border bg-background overflow-hidden">
            <div className="px-3 py-3 font-mono text-[13px] break-all select-all min-h-[3rem] flex items-center">
              {revealed ? token.token : masked}
            </div>
            <div className="border-t border-border bg-secondary/30 flex items-center justify-between px-2 py-1.5">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRevealed((r) => !r)}
                className="text-muted-foreground"
              >
                {revealed ? (
                  <EyeOff className="size-3" />
                ) : (
                  <Eye className="size-3" />
                )}
                {revealed ? 'Hide' : 'Reveal'}
              </Button>
              <Button size="sm" variant="ghost" onClick={copy}>
                {copied ? (
                  <Check className="size-3 text-primary" />
                ) : (
                  <Copy className="size-3" />
                )}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <Eyebrow>
              <Terminal className="size-3" /> Wire it up
            </Eyebrow>
          </div>
          <pre className="mt-2 font-mono text-[12px] bg-background border border-border rounded-md p-3 overflow-x-auto whitespace-pre leading-relaxed">
            <span className="text-muted-foreground"># uv / pip: match HAWK to the name in pyproject.toml</span>
            {'\n'}export UV_INDEX_HAWK_USERNAME=__token__
            {'\n'}export UV_INDEX_HAWK_PASSWORD='{revealed ? token.token : '****'}'
          </pre>
        </div>
      </div>

      <div className="border-t border-border bg-background/40 px-6 py-4 flex items-center justify-end gap-2">
        <Button onClick={onClose}>
          Done
          <ArrowRight className="size-4" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  )
}

/* ------------------------------- Helpers ------------------------------- */

function scopeList(mode: ScopeMode, selected: string[]): string[] {
  if (mode === 'none') return []
  if (mode === 'all') return ['*']
  return selected
}

function EmptyTokens({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="px-6 py-12 text-center">
      <div className="inline-flex items-center justify-center size-12 rounded-md bg-secondary border border-border mb-4">
        <KeyRound className="size-5 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="text-sm font-medium mb-1">No tokens yet</div>
      <p className="text-xs text-muted-foreground max-w-sm mx-auto leading-relaxed">
        Tokens authenticate CLI tools to Hawk over HTTP Basic with the username{' '}
        <code className="font-mono">__token__</code>.
      </p>
      <Button className="mt-5" onClick={onCreate}>
        <Plus className="size-4" />
        Create your first token
      </Button>
    </div>
  )
}
